import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { leadService } from '../../../services/leadService';
import { quoteService } from '../../../services/quoteService';
import { useAuth } from '../../../contexts/AuthContext';

const ServiceQuoteModal = ({ isOpen, onClose, service, onComplete }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    companyName: '',
    projectDescription: '',
    budget: '',
    timeline: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!formData?.customerName || !formData?.customerEmail || !formData?.customerPhone) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (!formData?.projectDescription) {
      setError('Veuillez décrire votre projet');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Step 1: Create or find lead (client)
      const leadData = {
        name: formData?.customerName,
        email: formData?.customerEmail,
        phone: formData?.customerPhone,
        service: service?.title,
        status: 'warm',
        source: 'website_quote_request',
        notes: `Entreprise: ${formData?.companyName || 'N/A'}\n${formData?.notes || ''}`
      };

      const createdLead = await leadService?.create(leadData);

      // Step 2: Create quote request
      const quoteData = {
        serviceId: service?.id,
        clientId: createdLead?.id,
        title: `Demande de devis - ${service?.title}`,
        description: formData?.projectDescription,
        status: 'draft',
        criteria: {
          budget: formData?.budget,
          timeline: formData?.timeline,
          companyName: formData?.companyName,
          projectDescription: formData?.projectDescription
        },
        basePrice: service?.basePrice || 0,
        subtotal: service?.basePrice || 0,
        totalAmount: service?.basePrice || 0,
        currency: 'USD',
        notes: formData?.notes,
        metadata: {
          requestedAt: new Date()?.toISOString(),
          source: 'website'
        }
      };

      await quoteService?.createPublic(quoteData);

      setSuccess(true);
      setTimeout(() => {
        onComplete?.();
      }, 2000);
    } catch (err) {
      console.error('Quote request error:', err);
      setError(err?.message || 'Erreur lors de la demande de devis');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  if (success) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
        <div className="bg-slate-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-md p-8 text-center">
          <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle2" size={40} className="text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Demande Envoyée!</h2>
          <p className="text-slate-400 mb-6">
            Votre demande de devis gratuite a été envoyée avec succès. Notre équipe vous contactera sous 24h.
          </p>
          <Button variant="default" fullWidth onClick={onComplete}>
            Fermer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="bg-slate-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-4xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h2 className="text-2xl font-bold text-white">Demander un Devis Gratuit</h2>
            <p className="text-sm text-slate-400 mt-1">
              Remplissez vos informations pour obtenir un devis personnalisé gratuit
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-slate-800 flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Détails du Service</h3>

                <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 shadow-lg shadow-blue-900/30 flex items-center justify-center">
                      <Icon name={service?.icon} size={24} className="text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">{service?.title}</h4>
                      <p className="text-sm text-slate-400">
                        {service?.category}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <Input
                    type="text"
                    label="Description du projet"
                    required
                    placeholder="Décrivez votre projet en détail"
                    value={formData?.projectDescription}
                    onChange={(e) => setFormData({ ...formData, projectDescription: e?.target?.value })}
                  />
                  <Input
                    type="text"
                    label="Budget estimé"
                    placeholder="Ex: 5000 - 10000 USD"
                    value={formData?.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e?.target?.value })}
                  />
                  <Input
                    type="text"
                    label="Délai souhaité"
                    placeholder="Ex: 2-3 mois"
                    value={formData?.timeline}
                    onChange={(e) => setFormData({ ...formData, timeline: e?.target?.value })}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Vos Informations</h3>
                <div className="space-y-4">
                  <Input
                    type="text"
                    label="Nom complet"
                    required
                    placeholder="Votre nom"
                    value={formData?.customerName}
                    onChange={(e) => setFormData({ ...formData, customerName: e?.target?.value })}
                  />
                  <Input
                    type="email"
                    label="Email"
                    required
                    placeholder="votre@email.com"
                    value={formData?.customerEmail}
                    onChange={(e) => setFormData({ ...formData, customerEmail: e?.target?.value })}
                  />
                  <Input
                    type="tel"
                    label="Téléphone"
                    required
                    placeholder=""
                    value={formData?.customerPhone}
                    onChange={(e) => setFormData({ ...formData, customerPhone: e?.target?.value })}
                  />
                  <Input
                    type="text"
                    label="Nom de l'entreprise (optionnel)"
                    placeholder="Votre entreprise"
                    value={formData?.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e?.target?.value })}
                  />
                  <Input
                    type="text"
                    label="Notes (optionnel)"
                    placeholder="Informations supplémentaires"
                    value={formData?.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
                  />
                </div>
              </div>

              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="CheckCircle2" size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">Demande 100% Gratuite</h4>
                    <p className="text-sm text-slate-400">
                      Aucun paiement requis. Recevez votre devis personnalisé gratuitement sous 24h.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-6">
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-6 pt-6 border-t border-white/10">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading}
              iconName={loading ? "Loader2" : "Send"}
              iconClassName={loading ? "animate-spin" : ""}
              iconPosition="left"
              className="flex-1"
            >
              {loading ? 'Envoi en cours...' : 'Envoyer la Demande Gratuite'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceQuoteModal;
